import {Badge, Box, Grid, Typography} from '@mui/material';
import {FilterType, SortTypeEnum} from 'api/common';
import {pascalCase} from 'change-case';
import AppIconButton from 'components/buttons/AppIconButton';
import {AppDrawer} from 'components/drawers';
import {AppSelect} from 'components/form';
import {AppTooltip} from 'components/toolip';
import {SelectOptionsType} from 'global';
import {useGetLookUps} from 'hooks';
import {useGetRemindersAPI, useGetRemindersCountAPI, useRemoveRemindersByIds} from 'hooks/reminders/useReminderApi';
import {LookupCategoryDescriptions, LookupCategoryTypes, ReminderModel, ReminderWhereQueryModel} from 'models';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AiTwotoneNotification} from 'react-icons/ai';
import {HiPlus} from 'react-icons/hi';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useSelector} from 'react-redux';
import {sessionSelector} from 'store/auth/sessionSlice';
import {appDrawerSelector} from 'store/themeSettings';
import {DrawerTypeValue} from 'types';
import {AddUpdateReminderDialog} from './dialogs';
import ReminderCard from './ReminderCard';
import ReminderDrawerSkeleton from './ReminderDrawerSkeleton';

interface ReminderDrawerProps {}

export const ReminderDrawer: React.FC<ReminderDrawerProps> = () => {
    const [reminderChange, setReminderChange] = useState<ReminderModel | undefined>();
    const {fetchRemindersCount, remindersCountResponse} = useGetRemindersCountAPI();
    const [reminderStatusOptions, setReminderStatusOptions] = useState<SelectOptionsType[]>([]);
    const {getLookups, getLookupsLoading, getLookupsResponse} = useGetLookUps();
    useEffect(() => {
        getLookups({
            variables: {
                where: {
                    type: {
                        eq: LookupCategoryDescriptions[LookupCategoryTypes.ReminderType],
                    },
                },
            },
        });
    }, []);

    useEffect(() => {
        if (!getLookupsLoading && getLookupsResponse) {
            const statuses = getLookupsResponse.nodes.map(item => ({
                label: pascalCase(item.title),
                value: item.title,
            })) as SelectOptionsType[];
            setReminderStatusOptions([{label: 'All', value: 'All'}, ...statuses]);
        }
    }, [getLookupsLoading, getLookupsResponse]);

    useEffect(() => {
        fetchRemindersCount({
            variables: {
                where: {
                    and: [
                        {
                            userId: {
                                eq: userId,
                            },
                        },
                        {
                            status: {
                                eq: 'Pending',
                            },
                        },
                    ],
                },
            },
        });
    }, [reminderChange]);

    const reminderChanges = (val: ReminderModel) => setReminderChange(val);
    const [selectedReminderStatus, setSelectedReminederStatus] = useState<string>('All');
    const [reminders, setReminders] = useState<ReminderModel[]>([]);
    const [removedId, setRemovedId] = useState<string | null>();
    const [openAddUpdateReminderDialog, setOpenAddUpdateReminderDialog] = useState(false);
    const [selectedReminder, setSelectedReminder] = useState<ReminderModel | null>(null);
    // Apis
    const {fetchReminders, remindersLoading, remindersResponse} = useGetRemindersAPI();
    const {removeReminder, removeReminderLoading, removeReminderResponse} = useRemoveRemindersByIds();
    const {isRightDrawerOpened} = useSelector(appDrawerSelector);
    const {userId} = useSelector(sessionSelector);
    const appendRemindersDataUnique = useCallback((value: ReminderModel[]) => {
        setReminders(prev => {
            if (prev) {
                const ids = prev.map(item => item.id);
                const newReminder = value.filter(item => !ids.includes(item.id));
                return [...prev, ...newReminder];
            }
            return value;
        });
    }, []);

    const generateWhereCondition = useMemo(() => {
        const whereCondition: FilterType<ReminderWhereQueryModel> = {
            and: [
                {
                    userId: {
                        eq: userId,
                    },
                },
                ...(selectedReminderStatus && selectedReminderStatus !== 'All'
                    ? [
                          {
                              status: {
                                  eq: selectedReminderStatus,
                              },
                          },
                      ]
                    : []),
            ],
        };
        return whereCondition;
    }, [selectedReminderStatus]);

    const handleNext = () => {
        fetchReminders({
            variables: {
                where: generateWhereCondition,
                first: 12,
                after: remindersResponse?.pageInfo.endCursor,
                order: [{reminderDate: SortTypeEnum.DESC}],
            },
        });
    };

    const handleReminderUpdate = (response: ReminderModel) => {
        setReminders(prev => {
            const ids = prev.map(item => item.id);
            if (ids.includes(response.id))
                return prev.map(item => {
                    if (item.id === response.id) {
                        return response;
                    }
                    return item;
                });
            else return [response, ...prev];
        });
    };

    // useEffects hooks

    useEffect(() => {
        setReminders([]);
        fetchReminders({
            variables: {
                where: generateWhereCondition,
                first: 12,
                order: [{reminderDate: SortTypeEnum.DESC}],
            },
        });
    }, [generateWhereCondition]);

    useEffect(() => {
        if (!removeReminderLoading && removeReminderResponse) {
            if (removedId) {
                setReminders(prev => prev.filter(item => item.id !== removedId));
            }
        }
    }, [removeReminderLoading, removeReminderResponse]);

    useEffect(() => {
        if (remindersResponse) {
            appendRemindersDataUnique(remindersResponse.nodes);
        }
    }, [remindersResponse]);

    return (
        <>
            <AppDrawer
                drawerType={DrawerTypeValue.Reminder}
                title="Reminders"
                showHeaderButton
                nodeBeforeTitle={
                    <AppTooltip title="Add Reminder">
                        <AppIconButton
                            color="primary"
                            variant="outlined"
                            onClick={() => setOpenAddUpdateReminderDialog(true)}
                        >
                            <HiPlus />
                        </AppIconButton>
                    </AppTooltip>
                }
                renderRightHeader={
                    <AppTooltip title="Status" placement="right-end">
                        <AppSelect
                            options={reminderStatusOptions}
                            sx={{
                                maxHeight: '30px',
                                minWidth: '100px',
                            }}
                            value={selectedReminderStatus}
                            onChange={event => setSelectedReminederStatus(event.target.value as string)}
                        />
                    </AppTooltip>
                }
                renderElement={(_open, handleOpen) => {
                    return (
                        <Box sx={{flexShrink: 0, ml: 0.75}}>
                            <AppTooltip title="Reminders" placement="left-start">
                                <AppIconButton
                                    onClick={handleOpen}
                                    elevation={isRightDrawerOpened === DrawerTypeValue.Reminder}
                                >
                                    <Badge
                                        badgeContent={remindersCountResponse ?? 0}
                                        color="primary"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                fontSize: '0.6rem',
                                                height: '13px',
                                                minWidth: '13px',
                                                padding: '0 4px',
                                            },
                                        }}
                                    >
                                        <AiTwotoneNotification />
                                    </Badge>
                                </AppIconButton>
                            </AppTooltip>
                        </Box>
                    );
                }}
            >
                {reminders && reminders.length === 0 && (
                    <Box textAlign="center" mt={6} color="#8a8a8a">
                        <AiTwotoneNotification size={48} style={{opacity: 0.3, marginBottom: 8}} />
                        <Typography variant="h6" fontWeight={700}>
                            No reminders found
                        </Typography>
                    </Box>
                )}
                {remindersLoading && !reminders ? (
                    <ReminderDrawerSkeleton />
                ) : (
                    <InfiniteScroll
                        height={'calc(100vh - 120px)'}
                        style={{
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'rgb(203, 211, 222) transparent',
                        }}
                        scrollableTarget="scrollableDiv"
                        dataLength={reminders.length ?? 0}
                        next={handleNext}
                        hasMore={remindersResponse?.pageInfo.hasNextPage ?? false}
                        loader="Loading More Reminders..."
                    >
                        <Grid container>
                            {reminders.map(data => {
                                return (
                                    <Grid item xs={12} key={data.id}>
                                        <ReminderCard
                                            handleDelete={id => {
                                                removeReminder([id]);
                                                setRemovedId(id);
                                            }}
                                            handleUpdate={() => setSelectedReminder(data)}
                                            reminder={data}
                                        />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </InfiniteScroll>
                )}
            </AppDrawer>
            {(openAddUpdateReminderDialog || !!selectedReminder) && (
                <AddUpdateReminderDialog
                    onSuccess={response => {
                        if (response) {
                            reminderChanges(response);
                            handleReminderUpdate(response);
                        }
                    }}
                    open={openAddUpdateReminderDialog || !!selectedReminder}
                    onCloseDialog={() => {
                        setSelectedReminder(null);
                        setOpenAddUpdateReminderDialog(false);
                    }}
                    reminder={selectedReminder}
                />
            )}
        </>
    );
};

export default ReminderDrawer;
